import { useState } from 'react'
import { Table } from 'components/table-compat/table-compat'
import {
    usePayrollWorkgroup,
    usePayrollWorkgroupDrill,
} from 'hooks/payroll/workgroup'
import {
    TableData,
    addLinkToFirstColumn,
    buildDataTableObject,
    insertRows,
    removeLinkFromFirstColumn,
} from 'utils/data'
import { extendWidthsForExtraColumns } from 'utils/table'
import { Container } from 'components/container/Container'
import { BasicPlaceholder } from 'components/placeholder'
import useRefresh from 'hooks/utils/useRefresh'
import useCountdown from 'hooks/utils/useCountdown'
import { ErrorAlert } from 'components/error-fallback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { translateTableData } from '../../../locales/utils'

export const Workgroup = () => {
    const [parent, setParent] = useState<string>('')
    const { t, currentLocation, env, logout, session, isEnglish } =
        useEssentials()
    const { payroll } = TRANSLATION_KEYS
    const { workgroup } = payroll
    let { data, isLoading, isError, refetch, remove, error } =
        usePayrollWorkgroup({
            env,
            accessToken: session?.accessToken ?? '',
            location: currentLocation,
        })
    useRefresh(refetch, remove)
    const count = useCountdown(5, logout, error as Error)

    const { data: subData } = usePayrollWorkgroupDrill({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        dynamicSubPath: parent,
    })

    if (data) {
        data = translateTableData({
            t,
            table: data,
            translation: {
                'work group': workgroup.workGroup,
                region_r_number: payroll.payrollRegion,
                'plan hrs': payroll.planHrs,
                'act+rem sched hrs': payroll.actRemSchedHrs,
            },
        })
    }

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    const baseWidths = isEnglish ? [120, 60, 110] : [100, 65, 125]
    const columnCount = data?.headings?.length ?? baseWidths.length
    const widths = extendWidthsForExtraColumns(baseWidths, columnCount, 100)

    const addChildRows = (data: TableData, subData: TableData) => {
        subData.rows = removeLinkFromFirstColumn(subData.rows)
        subData.rows = subData.rows.map((row) => {
            row[0].childOf = parent

            return row
        })
        const insertIndex =
            data.rows.findIndex((row) => row[0].value === parent) + 1
        const newData = { ...data }
        let newDataRows = [...data.rows]
        insertRows(newDataRows, insertIndex, subData.rows)
        newData.rows = newDataRows
        data = newData

        return data
    }
    if (data) {
        data.rows = addLinkToFirstColumn(data.rows)
        if (subData) {
            data = addChildRows(data, subData)
        }
    }

    const handleClick = (parentValue: string) => {
        if (data) {
            const match = data?.rows.filter(
                (row) => row[0].childOf === parentValue,
            )
            if (parent === parentValue || match.length > 0) {
                const filtered = data?.rows.filter(
                    (row) => row[0].childOf !== parentValue,
                )
                data.rows = filtered
                setParent('')
            } else {
                setParent(parentValue)
            }
        }
    }

    return (
        <Container className="hc8-pt-4x">
            <p className="hc8-fs-050">{t(payroll.workgroup.title)}</p>
            <Table
                data={buildDataTableObject({
                    currentData: data!,
                    widths,
                    handleClick,
                    pinFirstColumn: true,
                })}
                scrollableWidth={true}
            />
        </Container>
    )
}
